import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { ImageSettings } from './SiteCustomizationManager';
import { ApiService } from '../services/api';

interface ImageUploaderProps {
  images: ImageSettings;
  onChange: (images: ImageSettings) => void;
}

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_HERO_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_BACKGROUND_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

export const ImageUploader: React.FC<ImageUploaderProps> = ({ images, onChange }) => {
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);

  const validateFile = async (file: File, maxSize: number): Promise<string | null> => {
    // Check file type
    if (!ALLOWED_FORMATS.includes(file.type)) {
      return 'Invalid file format. Please upload PNG, JPG, or SVG files only.';
    }
    
    // Check file size
    if (file.size > maxSize) {
      return `File size exceeds ${maxSize / (1024 * 1024)}MB limit.`;
    }

    // Validate file signature (magic bytes) for non-SVG files
    if (file.type !== 'image/svg+xml') {
      try {
        const arrayBuffer = await file.slice(0, 12).arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        
        // Check magic bytes
        if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
          // JPEG: FF D8 FF
          if (bytes[0] !== 0xFF || bytes[1] !== 0xD8 || bytes[2] !== 0xFF) {
            return 'File appears to be corrupted or is not a valid JPEG image.';
          }
        } else if (file.type === 'image/png') {
          // PNG: 89 50 4E 47 0D 0A 1A 0A
          const pngSignature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
          if (!pngSignature.every((byte, index) => bytes[index] === byte)) {
            return 'File appears to be corrupted or is not a valid PNG image.';
          }
        }
      } catch (error) {
        console.error('Error validating file signature:', error);
        // Continue anyway - server will do final validation
      }
    }

    // Validate image dimensions
    try {
      const dimensions = await getImageDimensions(file);
      const maxDimension = 4000; // Maximum width or height
      
      if (dimensions.width > maxDimension || dimensions.height > maxDimension) {
        return `Image dimensions (${dimensions.width}x${dimensions.height}) exceed maximum of ${maxDimension}x${maxDimension} pixels.`;
      }
    } catch (error) {
      console.error('Error checking image dimensions:', error);
      // Continue anyway - server will do final validation
    }
    
    return null;
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  };

  const handleFileUpload = async (
    file: File,
    imageType: 'logo' | 'hero' | 'background',
    maxSize: number
  ) => {
    const validationError = await validateFile(file, maxSize);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(imageType);
    setError(null);

    try {
      const response = await ApiService.uploadSiteImage(file, imageType);
      
      if (response.success) {
        const newImages = { ...images };
        
        if (imageType === 'logo') {
          newImages.logo = {
            url: response.data.imageUrl,
            alt: 'Site Logo'
          };
        } else if (imageType === 'hero') {
          newImages.hero = {
            url: response.data.imageUrl,
            alt: 'Hero Image'
          };
        } else if (imageType === 'background') {
          newImages.background = {
            url: response.data.imageUrl,
            opacity: 0.5
          };
        }
        
        onChange(newImages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(null);
    }
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    imageType: 'logo' | 'hero' | 'background',
    maxSize: number
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, imageType, maxSize);
    }
    // Reset input value to allow re-uploading the same file
    event.target.value = '';
  };

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    imageType: 'logo' | 'hero' | 'background',
    maxSize: number
  ) => {
    event.preventDefault();
    setDragOver(null);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file, imageType, maxSize);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>, imageType: string) => {
    event.preventDefault();
    setDragOver(imageType);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleRemoveImage = (imageType: 'logo' | 'hero' | 'background') => {
    const newImages = { ...images };
    delete newImages[imageType];
    onChange(newImages);
  };

  const ImageUploadBox: React.FC<{
    title: string;
    description: string;
    imageType: 'logo' | 'hero' | 'background';
    maxSize: number;
    currentImage?: { url: string; alt: string } | { url: string; opacity: number };
    inputRef: React.RefObject<HTMLInputElement>;
  }> = ({ title, description, imageType, maxSize, currentImage, inputRef }) => (
    <Grid item xs={12} md={4}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {currentImage && (
          <Box sx={{ mb: 2, position: 'relative' }}>
            <Box
              component="img"
              src={currentImage.url}
              alt={'alt' in currentImage ? currentImage.alt : 'Background'}
              sx={{
                width: '100%',
                height: 150,
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: 1,
                backgroundColor: '#f5f5f5'
              }}
            />
            <IconButton
              onClick={() => handleRemoveImage(imageType)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)'
                }
              }}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}

        <Box
          onDrop={(e) => handleDrop(e, imageType, maxSize)}
          onDragOver={(e) => handleDragOver(e, imageType)}
          onDragLeave={handleDragLeave}
          sx={{
            border: '2px dashed',
            borderColor: dragOver === imageType ? 'primary.main' : '#ccc',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            backgroundColor: dragOver === imageType ? 'action.hover' : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onClick={() => inputRef.current?.click()}
        >
          {uploading === imageType ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Drag & drop or click to upload
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max size: {maxSize / (1024 * 1024)}MB
              </Typography>
            </>
          )}
        </Box>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          onChange={(e) => handleFileSelect(e, imageType, maxSize)}
          style={{ display: 'none' }}
        />

        {currentImage && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoveImage(imageType)}
            sx={{ mt: 2 }}
          >
            Remove Image
          </Button>
        )}
      </Paper>
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Image Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload custom images for your site. Supported formats: PNG, JPG, SVG.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <ImageUploadBox
          title="Logo"
          description="Your site logo (max 2MB)"
          imageType="logo"
          maxSize={MAX_LOGO_SIZE}
          currentImage={images.logo}
          inputRef={logoInputRef}
        />

        <ImageUploadBox
          title="Hero Image"
          description="Main banner image (max 5MB)"
          imageType="hero"
          maxSize={MAX_HERO_SIZE}
          currentImage={images.hero}
          inputRef={heroInputRef}
        />

        <ImageUploadBox
          title="Background Image"
          description="Page background (max 5MB)"
          imageType="background"
          maxSize={MAX_BACKGROUND_SIZE}
          currentImage={images.background}
          inputRef={backgroundInputRef}
        />
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Image Guidelines
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>Logo: Recommended size 200x80px, transparent background works best</li>
            <li>Hero Image: Recommended size 1920x600px for best quality</li>
            <li>Background: Recommended size 1920x1080px, will be displayed with opacity</li>
            <li>Use high-quality images for best results</li>
            <li>Optimize images before uploading to reduce file size</li>
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};
